<template>
  <span class="children:full">
    <span v-if="shouldRenderTextLink">
      <vf-link
        v-style:c="textLinkColor"
        v-style:display="textLinkDisplay"
        :aria-label="iconOnly ? text : undefined"
        class="c-$c space-x-1"
        :class="[
          { '@hover:c-$ch': textLinkHoverColor },
          textLinkStyle,
        ]"
        :data-cta="delocalizedTracking"
        :style="{ '--ch': textLinkHoverColor }"
        :target="targetAttribute"
        :to="fullUrl"
        :variant="textLinkUnderline === 'never' ? 'quieter' : (textLinkUnderline === 'hover' ? 'quiet' : undefined)"
      >
        <vf-icon
          v-if="icon && iconPosition !== 'right'"
          class="align-text-bottom i-block"
          :dir="icon.dir"
          :name="icon.icon"
          :size="iconSize"
        />
        <span :class="{ 'sr-only': iconOnly }">
          {{ text }}
        </span>
        <vf-icon
          v-if="icon && iconPosition === 'right'"
          class="align-text-bottom i-block"
          :dir="icon.dir"
          :name="icon.icon"
          :size="iconSize"
        />
      </vf-link>
    </span>

    <vf-button
      v-if="shouldRenderButton"
      v-style:display="buttonDisplay"
      :aria-label="iconOnly ? text : undefined"
      :class-content="[{ 'flex-row-reverse': iconPosition === 'right' }, 'gap-1']"
      :data-cta="delocalizedTracking"
      :size="buttonSize"
      :target="targetAttribute"
      :to="fullUrl"
      :variant="style"
    >
      <vf-icon v-if="icon" :dir="icon.dir" :name="icon.icon" :size="iconSize" />
      <span :class="{ 'sr-only': iconOnly }">
        {{ text }}
      </span>
    </vf-button>
  </span>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { ButtonSize } from '#types/components/vf/button'
import type { Target } from '#types/target'

const {
  delocalizedTracking,
  icon,
  iconPosition,
  iconSize = 'md',
  size = {} as Responsive<{ size?: ButtonSize, fullWidth?: boolean }>,
  style,
  targetAttribute,
  text,
  url = '',
} = defineProps<Target>()

const route = useRoute()
const router = useRouter()

const shouldRenderTextLink = computed(() => Object.values(style || {}).filter(Boolean).includes('text-link'))
const shouldRenderButton = computed(() => Object.values(style || {}).filter(Boolean).some((val) => val !== 'text-link'))

const textLinkDisplay = computed(() => {
  return Object.entries(style || {}).reduce((acc, [key, value]) => {
    acc[key] = value === 'text-link' ? 'inline' : 'none'
    return acc
  }, {})
})

const buttonDisplay = computed(() => {
  return Object.entries(style || {}).reduce((acc, [key, value]) => {
    acc[key] = value !== 'text-link' ? 'inline-flex' : 'none'
    return acc
  }, {})
})

const fullUrl = computed(() => {
  if (url.startsWith('#modal')) {
    return `${route.fullPath.toString()}${url}`
  }
  else if (url.startsWith('?') && url.includes('#modal')) {
    const [urlQuery, urlHash] = url.substring(1).split('#')
    const additionalParams = Object.fromEntries((new URLSearchParams(urlQuery)).entries())
    return router.resolve({
      query: { ...route.query, ...additionalParams },
      hash: urlHash ? `#${urlHash}` : ''
    }).href
  }
  return url
})

const { sm: { size: sm = 'sm' } = {}, md, lg } = size

const buttonSize: ButtonSize | Responsive<ButtonSize> = {
  sm,
  md: md?.size,
  lg: lg?.size
}
</script>
